// ─── NoticePage ───────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // eslint-disable-line no-unused-vars
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

export function NoticePage() {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  // Bundle 2 — reminders
  const [reminderModalNotice, setReminderModalNotice] = useState(null); // notice obj or null
  const [reminderForm, setReminderForm] = useState({
    startPreset: '1h',   // 1h | 4h | tomorrow9 | custom
    customStartMin: 60,
    cadencePreset: 'daily', // 1h | 3h | 12h | daily | custom
    customCadenceMin: 60,
    totalFires: 3,
    stopOnRead: true,
  });
  const [reminderSaving, setReminderSaving] = useState(false);

  // Deep-link highlight: push notifications set link_url=/notices?highlight=<id>.
  // We read it, scroll to that notice, and flash it briefly.
  const [highlightId, setHighlightId] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('highlight'); }
    catch { return null; }
  });

  useEffect(() => {
    load();
  }, []);

  // Once notices are loaded and we have a highlight target, scroll + flash it.
  useEffect(() => {
    if (!highlightId || loading || notices.length === 0) return;
    const el = document.getElementById(`notice-${highlightId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Clear the flash after a few seconds
      const t = setTimeout(() => setHighlightId(null), 4000);
      return () => clearTimeout(t);
    }
  }, [highlightId, loading, notices]);

  async function load() {
    try {
      const res = await api.get('/notices?limit=100');
      const list = res.data.notices || [];
      // Ordering rule:
      //   Tier 0 — UNREAD notices, newest first (float above everything, even pins)
      //   Tier 1 — read + PINNED, by pin_order (then recency)
      //   Tier 2 — read + unpinned, newest first
      // Once a notice is read it drops out of Tier 0 into its normal place.
      const tier = (n) => (!n.is_read ? 0 : (n.is_pinned ? 1 : 2));
      const ts = (n) => new Date(n.updated_at || n.created_at).getTime();
      list.sort((a, b) => {
        const ta = tier(a), tb = tier(b);
        if (ta !== tb) return ta - tb;
        // Within the pinned-read tier, respect pin_order first
        if (ta === 1) {
          const pa = a.pin_order ?? 0, pb = b.pin_order ?? 0;
          if (pa !== pb) return pa - pb;
        }
        // Otherwise newest first
        return ts(b) - ts(a);
      });
      setNotices(list);
    } finally {
      setLoading(false);
    }
  }

  async function markRead(id) {
    await api.patch(`/notices/${id}/read`).catch(() => {});
    setNotices(n => n.map(x => x.id === id ? { ...x, is_read: true } : x));
  }

  async function markAllRead() {
    await api.patch('/notices/read-all').catch(() => {});
    setNotices(n => n.map(x => ({ ...x, is_read: true })));
    toast.success('All marked as read');
  }

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function selectAll() {
    if (selected.size === notices.length) setSelected(new Set());
    else setSelected(new Set(notices.map(n => n.id)));
  }

  function exitSelectMode() {
    setSelectMode(false);
    setSelected(new Set());
  }

  async function bulkMarkRead() {
    if (selected.size === 0) return;
    setBulkSaving(true);
    try {
      const ids = [...selected];
      await api.post('/notices/bulk-mark-read', { notice_ids: ids });
      setNotices(n => n.map(x => ids.includes(x.id) ? { ...x, is_read: true } : x));
      toast.success(`${ids.length} marked as read`);
      exitSelectMode();
    } catch (err) {
      toast.error('Failed to mark as read');
    } finally { setBulkSaving(false); }
  }

  async function bulkMarkUnread() {
    if (selected.size === 0) return;
    setBulkSaving(true);
    try {
      const ids = [...selected];
      await api.post('/notices/bulk-mark-unread', { notice_ids: ids });
      setNotices(n => n.map(x => ids.includes(x.id) ? { ...x, is_read: false } : x));
      toast.success(`${ids.length} marked as unread`);
      exitSelectMode();
    } catch (err) {
      toast.error('Failed to mark as unread');
    } finally { setBulkSaving(false); }
  }

  async function bulkHide() {
    if (selected.size === 0) return;
    if (!window.confirm(`Remove ${selected.size} notice(s) from your view?\n\nThey will still be visible to other members and admins.`)) return;
    setBulkSaving(true);
    try {
      const ids = [...selected];
      await api.post('/notices/bulk-hide', { notice_ids: ids });
      setNotices(n => n.filter(x => !ids.includes(x.id)));
      toast.success(`${ids.length} removed from your view`);
      exitSelectMode();
    } catch (err) {
      toast.error('Failed to remove notices');
    } finally { setBulkSaving(false); }
  }

  // ─── Bundle 2 — Reminder handlers ─────────────
  function openReminderModal(notice) {
    // Seed form from existing reminder if any
    if (notice.my_reminder) {
      setReminderForm({
        startPreset: 'custom',
        customStartMin: 60,
        cadencePreset: 'custom',
        customCadenceMin: notice.my_reminder.cadence_minutes || 60,
        totalFires: Math.min(20, Math.max(1, (notice.my_reminder.total_fires || 3) - (notice.my_reminder.fires_done || 0))),
        stopOnRead: true,
      });
    } else {
      setReminderForm({
        startPreset: '1h', customStartMin: 60,
        cadencePreset: 'daily', customCadenceMin: 60,
        totalFires: 3, stopOnRead: true,
      });
    }
    setReminderModalNotice(notice);
  }
  function closeReminderModal() {
    setReminderModalNotice(null);
  }

  function computeStartInMinutes() {
    const f = reminderForm;
    if (f.startPreset === '1h') return 60;
    if (f.startPreset === '4h') return 240;
    if (f.startPreset === 'tomorrow9') {
      const now = new Date();
      const t = new Date(now); t.setDate(t.getDate()+1); t.setHours(9,0,0,0);
      return Math.max(1, Math.round((t - now) / 60000));
    }
    return Math.max(0, parseInt(f.customStartMin) || 0);
  }
  function computeCadenceMinutes() {
    const f = reminderForm;
    if (f.cadencePreset === '1h') return 60;
    if (f.cadencePreset === '3h') return 180;
    if (f.cadencePreset === '12h') return 720;
    if (f.cadencePreset === 'daily') return 1440;
    return Math.max(1, parseInt(f.customCadenceMin) || 60);
  }

  async function saveReminder() {
    if (!reminderModalNotice) return;
    const startIn = computeStartInMinutes();
    const cadence = computeCadenceMinutes();
    const total = Math.max(1, Math.min(20, parseInt(reminderForm.totalFires) || 1));

    // Client-side horizon check (backend also validates)
    const horizonMin = startIn + (total - 1) * cadence;
    if (horizonMin > 20 * 24 * 60) {
      toast.error('Schedule exceeds 20-day limit. Reduce cadence or count.');
      return;
    }

    setReminderSaving(true);
    try {
      const res = await api.post(`/notices/${reminderModalNotice.id}/remind`, {
        start_in_minutes: startIn,
        cadence_minutes: cadence,
        total_fires: total,
        stop_on_read: reminderForm.stopOnRead,
      });
      // Update the notice in-place with new reminder data
      setNotices(ns => ns.map(n => n.id === reminderModalNotice.id ? {
        ...n,
        my_reminder: {
          id: res.data.reminder.id,
          next_fire_at: res.data.reminder.next_fire_at,
          fires_done: 0,
          total_fires: total,
          cadence_minutes: cadence,
        }
      } : n));
      toast.success(total === 1 ? 'Reminder set' : `${total} reminders scheduled`);
      closeReminderModal();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to set reminder');
    } finally { setReminderSaving(false); }
  }

  async function cancelReminder(notice) {
    if (!notice?.my_reminder) return;
    if (!window.confirm('Cancel this reminder?')) return;
    try {
      await api.delete(`/notices/${notice.id}/remind`);
      setNotices(ns => ns.map(n => n.id === notice.id ? { ...n, my_reminder: null } : n));
      toast.success('Reminder cancelled');
    } catch (err) {
      toast.error('Failed to cancel reminder');
    }
  }

  const typeIcons = {
    personal_system: '🔔', personal_admin: '📨', broadcast: '📣',
    monday_fine_report: '📋', motm_announcement: '🏆', birthday: '🎂',
    anniversary: '💍', availability_prompt: '✋', opt_out_broadcast: '🙋',
    floater_chart: '📊', unmatched_payment: '💳', series_announcement: '🏅',
    achievement: '🏆',
  };

  function getNoticeIcon(notice) {
    // For birthday/anniversary type notices that are actually achievements/awards
    if (['birthday', 'anniversary'].includes(notice.notice_type)) {
      const t = (notice.title || '').toLowerCase();
      if (t.includes('champion') || t.includes('winner') || t.includes('cup')) return '🏆';
      if (t.includes('best bowler') || t.includes('bowler')) return '🎳';
      if (t.includes('best batsman') || t.includes('batsman') || t.includes('century') || t.includes('fifty')) return '🏏';
      if (t.includes('fielder') || t.includes('catch')) return '🧤';
      if (t.includes('wicket keeper') || t.includes('keeper')) return '🧤';
      if (t.includes('man of the match') || t.includes('man of the series') || t.includes('motm')) return '⭐';
      if (t.includes('all-rounder') || t.includes('allrounder')) return '⚡';
      if (t.includes('hat-trick') || t.includes('hat trick')) return '🎯';
      if (t.includes('anniversary')) return '💍';
      if (t.includes('birthday')) return '🎂';
      return '🏆'; // default for non-birthday/anniversary content
    }
    return typeIcons[notice.notice_type] || '📨';
  }

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  const unread = notices.filter(n => !n.is_read).length;

  return (
    <div>
      <div style={{ background:"white", borderBottom:"1px solid #e5e7eb", padding:"18px 16px 14px", position:"relative", overflow:"hidden" }} style={{ paddingBottom: 'var(--space-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap:'wrap', gap:8 }}>
          <h2 style={{ color: '#F5C518', fontFamily: 'Rajdhani,sans-serif' }}>
            🔔 Noticeboard
          </h2>
          <div style={{ display:'flex', gap:8 }}>
            {unread > 0 && !selectMode && (
              <button onClick={markAllRead} style={{ background: 'none', border: 'none', color: '#1E3A6E', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 }}>
                Mark all read
              </button>
            )}
            <button onClick={() => selectMode ? exitSelectMode() : setSelectMode(true)}
              style={{ background: selectMode ? '#F5C518' : 'white', color: selectMode ? '#1a1a1a' : '#1E3A6E',
                border:'1px solid #1E3A6E', padding:'4px 12px', borderRadius:16,
                fontSize:'0.75rem', fontWeight:700, cursor:'pointer' }}>
              {selectMode ? 'Cancel' : '☑ Select'}
            </button>
          </div>
        </div>
        {unread > 0 && !selectMode && <p style={{ color: '#1E3A6E', fontSize: '0.85rem', marginTop: 4, fontWeight: 600 }}>{unread} unread</p>}
        {selectMode && (
          <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:8, fontSize:'0.78rem', color:'#1E3A6E', fontWeight:600 }}>
            <button onClick={selectAll}
              style={{ background:'#1E3A6E', border:'none', color:'white', padding:'4px 10px', borderRadius:12, fontSize:'0.72rem', fontWeight:700, cursor:'pointer' }}>
              {selected.size === notices.length && notices.length > 0 ? 'Deselect All' : `Select All (${notices.length})`}
            </button>
            <span>{selected.size} selected</span>
          </div>
        )}
      </div>

      <div className="container" style={{ paddingTop: 'var(--space-md)', paddingBottom: selectMode && selected.size > 0 ? 100 : 16 }}>
        {notices.length === 0 ? (
          <div className="empty-state"><div className="empty-state__icon">🔔</div><div className="empty-state__title">No notices yet</div></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {notices.map(notice => {
              // Consider it edited only if updated_at is meaningfully after created_at (60s buffer)
              const isEdited = !!notice.updated_at
                && new Date(notice.updated_at).getTime() > new Date(notice.created_at).getTime() + 60_000;
              const isSel = selected.has(notice.id);
              const isHighlighted = highlightId && String(notice.id) === String(highlightId);
              return (
                <div
                  key={notice.id}
                  id={`notice-${notice.id}`}
                  onClick={() => {
                    if (selectMode) toggleSelect(notice.id);
                    else if (!notice.is_read) markRead(notice.id);
                  }}
                  style={{
                    cursor: (selectMode || !notice.is_read) ? 'pointer' : 'default',
                    background: isSel ? '#dbeafe' : (notice.is_read ? 'white' : 'var(--info-bg)'),
                    border: '2px solid ' + (isHighlighted ? '#F5C518' : (isSel ? '#1E3A6E' : 'var(--border, #e5e7eb)')),
                    borderRadius: 12,
                    boxShadow: isHighlighted ? '0 0 0 4px rgba(245,197,24,0.35)' : '0 1px 3px rgba(0,0,0,0.04)',
                    padding: 14,
                    display: 'flex',
                    gap: 10,
                    lineHeight: 1.5,
                    transition: 'box-shadow 0.4s ease, border-color 0.4s ease',
                  }}
                >
                  {selectMode && (
                    <div style={{ width:22, height:22, borderRadius:4, border:'2px solid', flexShrink:0,
                      borderColor: isSel ? '#1E3A6E' : '#d1d5db',
                      background: isSel ? '#1E3A6E' : 'white',
                      display:'flex', alignItems:'center', justifyContent:'center', alignSelf:'flex-start', marginTop:2 }}>
                      {isSel && <span style={{ color:'white', fontSize:'0.78rem', fontWeight:900 }}>✓</span>}
                    </div>
                  )}
                  <div style={{ fontSize: '1.3rem', flexShrink: 0 }}>{getNoticeIcon(notice)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      {!notice.is_read && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#F5C518', flexShrink: 0 }} />}
                      <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{notice.title}</span>
                      {isEdited && (
                        <span style={{
                          fontSize: '0.62rem', fontWeight: 800, letterSpacing: 0.3,
                          background: '#fb923c', color: 'white', padding: '2px 7px',
                          borderRadius: 10, whiteSpace: 'nowrap',
                        }}>🔄 UPDATED</span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--ucc-gray-600)', whiteSpace: 'pre-line' }}>{notice.body}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--ucc-gray-400)', marginTop: 6 }}>
                      {'Posted ' + new Date(notice.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      {isEdited && (' · Updated ' + new Date(notice.updated_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }))}
                    </div>
                    {/* Bundle 2 — Reminder pill */}
                    {!selectMode && (
                      <div style={{ marginTop:8, display:'flex', gap:6, flexWrap:'wrap' }}>
                        {notice.my_reminder ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); cancelReminder(notice); }}
                            style={{
                              background:'#fef3c7', color:'#92400e',
                              border:'1px solid #fbbf24', borderRadius:14,
                              padding:'3px 10px', fontSize:'0.68rem', fontWeight:700,
                              cursor:'pointer', whiteSpace:'nowrap',
                            }}
                            title="Tap to cancel">
                            🔔 {(notice.my_reminder.total_fires - notice.my_reminder.fires_done)} more · next {new Date(notice.my_reminder.next_fire_at).toLocaleString('en-IN', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
                          </button>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); openReminderModal(notice); }}
                            style={{
                              background:'white', color:'#1E3A6E',
                              border:'1px solid #cbd5e1', borderRadius:14,
                              padding:'3px 10px', fontSize:'0.68rem', fontWeight:600,
                              cursor:'pointer', whiteSpace:'nowrap',
                            }}>
                            🔔 Remind Me
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sticky footer — bulk actions */}
      {selectMode && selected.size > 0 && (
        <div style={{ position:'fixed', bottom:'calc(64px + env(safe-area-inset-bottom, 0px))', left:0, right:0, background:'white',
          borderTop:'2px solid #1E3A6E', padding:'10px 14px', zIndex:150,
          boxShadow:'0 -4px 16px rgba(0,0,0,0.1)', display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
          <span style={{ fontSize:'0.75rem', fontWeight:700, color:'#1E3A6E', marginRight:4 }}>
            {selected.size} selected
          </span>
          <div style={{ marginLeft:'auto', display:'flex', gap:6, flexWrap:'wrap' }}>
            <button onClick={bulkMarkRead} disabled={bulkSaving}
              style={{ padding:'8px 12px', borderRadius:8, border:'none',
                background:'#16a34a', color:'white', fontSize:'0.72rem', fontWeight:700,
                cursor: bulkSaving ? 'wait' : 'pointer', opacity: bulkSaving ? 0.6 : 1 }}>
              ✓ Read
            </button>
            <button onClick={bulkMarkUnread} disabled={bulkSaving}
              style={{ padding:'8px 12px', borderRadius:8, border:'none',
                background:'#F5C518', color:'#1a1a1a', fontSize:'0.72rem', fontWeight:700,
                cursor: bulkSaving ? 'wait' : 'pointer', opacity: bulkSaving ? 0.6 : 1 }}>
              ● Unread
            </button>
            <button onClick={bulkHide} disabled={bulkSaving}
              style={{ padding:'8px 12px', borderRadius:8, border:'none',
                background:'#dc2626', color:'white', fontSize:'0.72rem', fontWeight:700,
                cursor: bulkSaving ? 'wait' : 'pointer', opacity: bulkSaving ? 0.6 : 1 }}>
              🗑 Hide
            </button>
          </div>
        </div>
      )}

      {/* Bundle 2 — Reminder setup modal */}
      {reminderModalNotice && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:300,
          display:'flex', alignItems:'flex-end', justifyContent:'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) closeReminderModal(); }}>
          <div style={{ background:'white', borderRadius:'18px 18px 0 0', width:'100%', maxWidth:480,
            maxHeight:'88vh', display:'flex', flexDirection:'column' }}>
            <div style={{ overflowY:'auto', padding:'20px 20px 0', flex:1 }}>
              <div style={{ width:40, height:4, background:'#e5e7eb', borderRadius:2, margin:'0 auto 14px' }} />
              <div style={{ fontSize:'1.05rem', fontWeight:800, color:'#1E3A6E', marginBottom:4 }}>
                🔔 Remind Me
              </div>
              <div style={{ fontSize:'0.8rem', color:'#6b7280', marginBottom:14,
                whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                {reminderModalNotice.title}
              </div>

              {/* When to start */}
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#374151', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.06em' }}>
                  When to start
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
                  {[
                    { v:'1h', label:'In 1 hour' },
                    { v:'4h', label:'In 4 hours' },
                    { v:'tomorrow9', label:'Tomorrow 9 AM' },
                    { v:'custom', label:'Custom' },
                  ].map(opt => (
                    <button key={opt.v}
                      onClick={() => setReminderForm(f => ({ ...f, startPreset: opt.v }))}
                      style={{
                        padding:'8px 10px', borderRadius:8, fontSize:'0.78rem', fontWeight:600, cursor:'pointer',
                        border: reminderForm.startPreset === opt.v ? '2px solid #1E3A6E' : '1px solid #d1d5db',
                        background: reminderForm.startPreset === opt.v ? '#dbeafe' : 'white',
                        color: reminderForm.startPreset === opt.v ? '#1E3A6E' : '#374151',
                      }}>{opt.label}</button>
                  ))}
                </div>
                {reminderForm.startPreset === 'custom' && (
                  <div style={{ marginTop:8, display:'flex', alignItems:'center', gap:8 }}>
                    <input type="number" min="0" max="28800"
                      value={reminderForm.customStartMin}
                      onChange={e => setReminderForm(f => ({ ...f, customStartMin: e.target.value }))}
                      style={{ flex:1, padding:'8px', borderRadius:6, border:'1px solid #d1d5db', fontSize:'0.85rem' }}
                    />
                    <span style={{ fontSize:'0.8rem', color:'#6b7280' }}>minutes</span>
                  </div>
                )}
              </div>

              {/* Frequency */}
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#374151', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.06em' }}>
                  Frequency
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:6 }}>
                  {[
                    { v:'1h', label:'Hourly' },
                    { v:'3h', label:'Every 3h' },
                    { v:'12h', label:'Every 12h' },
                    { v:'daily', label:'Daily' },
                    { v:'custom', label:'Custom' },
                  ].map(opt => (
                    <button key={opt.v}
                      onClick={() => setReminderForm(f => ({ ...f, cadencePreset: opt.v }))}
                      style={{
                        padding:'8px 6px', borderRadius:8, fontSize:'0.75rem', fontWeight:600, cursor:'pointer',
                        border: reminderForm.cadencePreset === opt.v ? '2px solid #1E3A6E' : '1px solid #d1d5db',
                        background: reminderForm.cadencePreset === opt.v ? '#dbeafe' : 'white',
                        color: reminderForm.cadencePreset === opt.v ? '#1E3A6E' : '#374151',
                      }}>{opt.label}</button>
                  ))}
                </div>
                {reminderForm.cadencePreset === 'custom' && (
                  <div style={{ marginTop:8, display:'flex', alignItems:'center', gap:8 }}>
                    <input type="number" min="1" max="43200"
                      value={reminderForm.customCadenceMin}
                      onChange={e => setReminderForm(f => ({ ...f, customCadenceMin: e.target.value }))}
                      style={{ flex:1, padding:'8px', borderRadius:6, border:'1px solid #d1d5db', fontSize:'0.85rem' }}
                    />
                    <span style={{ fontSize:'0.8rem', color:'#6b7280' }}>minutes between reminders</span>
                  </div>
                )}
              </div>

              {/* How many reminders */}
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#374151', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.06em' }}>
                  How many reminders (max 20)
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <input type="range" min="1" max="20"
                    value={reminderForm.totalFires}
                    onChange={e => setReminderForm(f => ({ ...f, totalFires: parseInt(e.target.value) }))}
                    style={{ flex:1 }}
                  />
                  <div style={{
                    minWidth:40, textAlign:'center', padding:'4px 10px', borderRadius:6,
                    background:'#1E3A6E', color:'white', fontSize:'0.9rem', fontWeight:700 }}>
                    {reminderForm.totalFires}
                  </div>
                </div>
              </div>

              {/* Stop on read */}
              <label style={{ display:'flex', alignItems:'center', gap:8, padding:'10px', background:'#f9fafb',
                borderRadius:8, cursor:'pointer', marginBottom:12 }}>
                <input type="checkbox"
                  checked={reminderForm.stopOnRead}
                  onChange={e => setReminderForm(f => ({ ...f, stopOnRead: e.target.checked }))}
                  style={{ width:18, height:18 }}
                />
                <span style={{ fontSize:'0.82rem', color:'#374151' }}>
                  Stop reminding me once I've read this notice
                </span>
              </label>

              <div style={{ background:'#eff6ff', border:'1px solid #93c5fd', borderRadius:8,
                padding:10, fontSize:'0.72rem', color:'#1e40af', marginBottom:10 }}>
                {(() => {
                  const startIn = computeStartInMinutes();
                  const cadence = computeCadenceMinutes();
                  const total = reminderForm.totalFires;
                  const horizonDays = Math.ceil((startIn + (total-1)*cadence) / (60*24));
                  return `Summary: first reminder in ${startIn < 60 ? startIn+' min' : Math.round(startIn/60)+' hr'}, every ${cadence < 60 ? cadence+' min' : cadence%60===0 ? (cadence/60)+' hr' : cadence+' min'}, ${total} total. Runs over ~${horizonDays} day${horizonDays!==1?'s':''}.`;
                })()}
              </div>
            </div>

            <div style={{ padding:'10px 20px', paddingBottom:'calc(14px + env(safe-area-inset-bottom, 0px))',
              borderTop:'1px solid #f3f4f6', display:'flex', gap:8, flexShrink:0 }}>
              <button onClick={closeReminderModal}
                style={{ flex:1, padding:'10px', borderRadius:10, border:'1px solid #e5e7eb',
                  background:'white', color:'#374151', fontSize:'0.85rem', fontWeight:700, cursor:'pointer' }}>
                Cancel
              </button>
              <button onClick={saveReminder} disabled={reminderSaving}
                style={{ flex:2, padding:'10px', borderRadius:10, border:'none',
                  background: reminderSaving ? '#9ca3af' : '#1E3A6E', color:'white',
                  fontSize:'0.85rem', fontWeight:700,
                  cursor: reminderSaving ? 'wait' : 'pointer' }}>
                {reminderSaving ? 'Saving…' : '🔔 Schedule Reminders'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AttendancePage ───────────────────────────────────────
export function AttendancePage() {
  const [summary, setSummary] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/attendance/my?year=2026')
      .then(res => { setSummary(res.data.summary); setAttendance(res.data.attendance || []); })
      .finally(() => setLoading(false));
  }, []);

  const STATUS_CONFIG = {
    present: { label: 'Present', color: 'var(--success)', bg: 'var(--success-bg)' },
    absent: { label: 'Absent', color: 'var(--danger)', bg: 'var(--danger-bg)' },
    rested: { label: 'Rested', color: 'var(--info)', bg: 'var(--info-bg)' },
    series_rested: { label: 'Series Rested', color: '#7c3aed', bg: '#ede9fe' },
    opt_out_fee_only: { label: 'Opt Out', color: 'var(--warning)', bg: 'var(--warning-bg)' },
    opt_out_full: { label: 'Opt Out', color: 'var(--success)', bg: 'var(--success-bg)' },
    exempted: { label: 'Exempted', color: 'var(--info)', bg: 'var(--info-bg)' },
    holiday: { label: 'Holiday', color: 'var(--ucc-gray-500)', bg: 'var(--ucc-gray-100)' },
  };

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ background:"white", borderBottom:"1px solid #e5e7eb", padding:"18px 16px 14px", position:"relative", overflow:"hidden" }}>
        <h2 style={{ color: '#F5C518', fontFamily: 'Rajdhani,sans-serif', marginBottom: 4 }}>📅 My Attendance</h2>
        <p style={{ color: 'rgba(255,255,255,.6)', fontSize: '0.85rem' }}>Season 2026</p>
      </div>
      <div className="container" style={{ paddingTop: 'var(--space-lg)' }}>
        {summary && (
          <>
            <div className="stat-grid stat-grid-3 mb-md">
              <div className="stat-box">
                <div className="stat-box__value" style={{ color: summary.percentage < 50 ? 'var(--danger)' : 'var(--success)' }}>{summary.percentage}%</div>
                <div className="stat-box__label">Attendance</div>
              </div>
              <div className="stat-box">
                <div className="stat-box__value">{summary.present}</div>
                <div className="stat-box__label">Present</div>
              </div>
              <div className="stat-box">
                <div className="stat-box__value">{summary.absent}</div>
                <div className="stat-box__label">Absent</div>
              </div>
            </div>
            <div className="progress-bar mb-md">
              <div className={`progress-bar__fill ${summary.percentage < 50 ? 'progress-bar__fill--danger' : 'progress-bar__fill--success'}`}
                style={{ width: `${summary.percentage}%` }} />
            </div>
            {summary.below_threshold && (
              <div className="alert alert-danger mb-md">
                <span>⚠️</span>
                <div><strong>Below 50% threshold</strong><br /><span style={{ fontSize: '0.85rem' }}>Annual awards require minimum 50% attendance.</span></div>
              </div>
            )}
          </>
        )}
        <div className="card">
          {attendance.map(a => {
            const cfg = STATUS_CONFIG[a.status] || STATUS_CONFIG.absent;
            return (
              <div key={a.id} className="list-item">
                <div style={{ width: 10, height: 40, borderRadius: 4, background: cfg.bg, border: `2px solid ${cfg.color}`, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    {new Date(a.match_date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--ucc-gray-500)' }}>
                    {a.match_type} Rules {a.checkin_time && `• Checked in: ${new Date(a.checkin_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`}
                  </div>
                </div>
                <span style={{ background: cfg.bg, color: cfg.color, padding: '3px 10px', borderRadius: 'var(--radius-full)', fontSize: '0.75rem', fontWeight: 700 }}>
                  {cfg.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── CheckinPage ──────────────────────────────────────────
// Session 15 Turn 3 — 5 states: not_match_day / open / closed_needs_override / override_granted / already_checked_in
export function CheckinPage() {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [match, setMatch] = useState(null);
  const [windowState, setWindowState] = useState(null); // { state, match, lateMins, cutoff, checkin_time?, checkin_source? }
  const [loading, setLoading] = useState(true);

  async function refreshWindow(mId) {
    if (!mId) return;
    try {
      const res = await api.get('/attendance/checkin-window/' + mId);
      setWindowState(res.data);
    } catch (e) {
      // If endpoint 404s or errors, fall back to whatever we had
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const upcoming = await api.get('/matches/upcoming-sunday');
        const m = upcoming.data.match;
        setMatch(m);
        if (m) await refreshWindow(m.id);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // The venue drives this. A NULL venue means the club's own ground — that is
  // how every historical match is stored, so absence of a venue must read as
  // home, never as away.
  const isAwayMatch = !!(match && match.venue_id && match.venue_is_default !== true);

  async function handleCheckin() {
    if (!match) return toast.error('No match found');
    setChecking(true);
    try {
      const pos = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 15000 })
      );
      const res = await api.post('/attendance/checkin', {
        match_id: match.id,
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
      });
      setResult(res.data);
      await refreshWindow(match.id);
    } catch (err) {
      if (err.code === 1) { toast.error('Location permission denied. Please enable location access.'); }
      else if (err.code === 2) { toast.error('Could not get your location. Please try again.'); }
      else { toast.error(err.response?.data?.error || 'Check-in failed. Try again.'); }
    } finally {
      setChecking(false);
    }
  }

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  const state = windowState?.state || (match ? 'not_match_day' : 'no_match');

  return (
    <div>
      <div style={{ background:"white", borderBottom:"1px solid #e5e7eb", padding:"18px 16px 14px", position:"relative", overflow:"hidden" }}>
        <h2 style={{ color: '#F5C518', fontFamily: 'Rajdhani,sans-serif', marginBottom: 4 }}>📍 GPS Check-In</h2>
        <p style={{ color: 'rgba(255,255,255,.6)', fontSize: '0.85rem' }}>Must be within 15m of the ground · Check-in closes 30 min after reporting time</p>
      </div>
      <div className="container" style={{ paddingTop: 'var(--space-lg)', textAlign: 'center' }}>

        {/* Success confirmation after a fresh check-in (shown briefly) */}
        {result && (
          <div>
            <div style={{ fontSize: '5rem', marginBottom: 'var(--space-md)' }}>{result.lateMins > 0 ? '⚠️' : '✅'}</div>
            <h3 style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: '1.5rem', marginBottom: 'var(--space-sm)' }}>
              {result.lateMins > 0 ? 'Checked In Late' : 'Checked In!'}
            </h3>
            <p style={{ color: 'var(--ucc-gray-600)', marginBottom: 'var(--space-lg)' }}>{result.message}</p>
            {result.lateFine && (
              <div className="alert alert-info" style={{ textAlign: 'left' }}>
                <span>ℹ️</span>
                <div><strong>Late check-in recorded</strong><br/>
                  <span style={{ fontSize: '0.85rem' }}>
                    Fine, if any, is at the Admin's discretion. If you had taken prior permission to come late, please mention it to the admin for consideration.
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Otherwise render based on the window state */}
        {!result && (<>
          {state === 'no_match' && (
            <div className="empty-state"><div className="empty-state__icon">📅</div><div className="empty-state__title">No match scheduled</div></div>
          )}

          {state === 'cancelled' && (
            <div className="alert alert-info">
              <span>ℹ️</span>
              <div>This Sunday's match has been cancelled.</div>
            </div>
          )}

          {state === 'not_match_day' && (
            <div className="alert alert-info">
              <span>ℹ️</span>
              <div>Check-in is only available on match day ({match && new Date(match.match_date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })})</div>
            </div>
          )}

          {state === 'already_checked_in' && (
            <div>
              <div style={{ fontSize:'5rem', marginBottom:'var(--space-md)' }}>✅</div>
              <h3 style={{ fontFamily:'Rajdhani,sans-serif', fontSize:'1.5rem', marginBottom:'var(--space-sm)' }}>Checked In</h3>
              <p style={{ color: 'var(--ucc-gray-600)' }}>
                {windowState.checkin_time && `You checked in at ${new Date(windowState.checkin_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`}
                {windowState.checkin_source ? ` (${windowState.checkin_source})` : ''}
              </p>
            </div>
          )}

          {/* AWAY MATCH — an admin marks attendance, so no button at all.
              GPS check-in is fenced to the club's own ground; offering a
              button here would only produce a refusal telling the member they
              are three thousand kilometres away. */}
          {isAwayMatch && (
            <div>
              <div style={{ fontSize: '4rem', marginBottom: 'var(--space-md)' }}>✈️</div>
              <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1.15rem',
                color: 'var(--ucc-navy)', marginBottom: 6 }}>
                {match?.venue || 'Away match'}
              </div>
              <p style={{ color: 'var(--ucc-gray-600)', fontSize: '0.9rem', lineHeight: 1.6,
                maxWidth: 380, margin: '0 auto' }}>
                There's no check-in for matches away from the UCC Ground.
                An admin will mark attendance on the day.
              </p>
            </div>
          )}

          {!isAwayMatch && state === 'open' && (
            <div>
              <div style={{ fontSize: '5rem', marginBottom: 'var(--space-lg)' }}>📍</div>
              <p style={{ color: 'var(--ucc-gray-600)', marginBottom: 'var(--space-xl)', fontSize: '0.9rem' }}>
                Tap below to check in using GPS. Make sure you're at the ground.
              </p>
              <button
                className="btn btn-primary btn-lg"
                style={{ padding: '0 48px', fontSize: '1.1rem' }}
                onClick={handleCheckin}
                disabled={checking}
              >
                {checking ? '📡 Getting location…' : '📍 Check In Now'}
              </button>
              <p style={{ color: 'var(--ucc-gray-400)', fontSize: '0.75rem', marginTop: 'var(--space-md)' }}>
                Biometric check-in also available at the pavilion
              </p>
            </div>
          )}

          {!isAwayMatch && state === 'override_granted' && (
            <div>
              <div style={{ background:'#fef3c7', border:'1px solid #fbbf24', borderRadius:10, padding:'12px 14px', marginBottom:20, textAlign:'left', color:'#92400e' }}>
                <strong>⚠️ Admin has granted you late check-in.</strong><br/>
                <span style={{ fontSize:'0.85rem' }}>
                  You are {windowState.lateMins} minutes late (past the {windowState.cutoff}-minute cutoff). A late fine, if any, will be at the Admin's discretion.
                </span>
              </div>
              <button
                className="btn btn-primary btn-lg"
                style={{ padding: '0 48px', fontSize: '1.1rem' }}
                onClick={handleCheckin}
                disabled={checking}
              >
                {checking ? '📡 Getting location…' : '📍 Check In Now'}
              </button>
            </div>
          )}

          {state === 'closed_needs_override' && (
            <div>
              <div style={{ fontSize: '5rem', marginBottom: 'var(--space-md)' }}>🔒</div>
              <h3 style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: '1.5rem', marginBottom: 'var(--space-sm)' }}>
                Check-In Closed
              </h3>
              <p style={{ color: 'var(--ucc-gray-600)', marginBottom: 'var(--space-md)', fontSize: '0.9rem' }}>
                You are {windowState.lateMins} minutes late (cutoff: {windowState.cutoff} min).
                <br />Contact an admin if you need to check in.
              </p>
            </div>
          )}
        </>)}
      </div>
    </div>
  );
}

// ─── FloaterPage ──────────────────────────────────────────
export function FloaterPage() {
  const [chart, setChart] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/floaters/chart').then(res => setChart(res.data.chart || [])).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  const tier1 = chart.filter(r => r.tier1 === true || r.tier1 === 'true');
  const tier2 = chart.filter(r => !(r.tier1 === true || r.tier1 === 'true'));

  return (
    <div>
      <div style={{ background:"white", borderBottom:"1px solid #e5e7eb", padding:"18px 16px 14px", position:"relative", overflow:"hidden" }}>
        <h2 style={{ color: '#F5C518', fontFamily: 'Rajdhani,sans-serif', marginBottom: 4 }}>🔄 Floater Chart</h2>
        <p style={{ color: 'rgba(255,255,255,.6)', fontSize: '0.85rem' }}>Season 2026 • UCC Rules matches only</p>
      </div>
      <div className="container" style={{ paddingTop: 'var(--space-md)' }}>
        <div className="alert alert-info mb-md">
          <span>ℹ️</span>
          <div style={{ fontSize: '0.85rem' }}>Tier 1 (never floated) gets priority. Floater not applicable during series or ICC Sundays.</div>
        </div>
        {tier1.length > 0 && (
          <>
            <div className="section-title mb-sm" style={{ color: 'var(--success)' }}>🌟 Tier 1 — Never Floated</div>
            <div className="card mb-md">
              {tier1.map(r => (
                <div key={r.id} className="list-item">
                  <div className="avatar">{r.display_name?.charAt(0)}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{r.display_name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--ucc-gray-500)' }}>{r.ucc_id}</div>
                  </div>
                  <span className="badge badge-success">TIER 1</span>
                </div>
              ))}
            </div>
          </>
        )}
        {tier2.length > 0 && (
          <>
            <div className="section-title mb-sm">Tier 2 — Floated Before</div>
            <div className="card">
              {tier2.map((r, i) => (
                <div key={r.id} className="list-item">
                  <div className="rank-badge rank-other" style={{ fontSize: '0.75rem' }}>{i + 1}</div>
                  <div className="avatar">{r.display_name?.charAt(0)}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{r.display_name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--ucc-gray-500)' }}>
                      Floated {r.times_floated}x • Last: {r.last_floated_date ? new Date(r.last_floated_date).toLocaleDateString('en-IN', { day:'numeric', month:'short' }) : 'N/A'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── CharterPage ──────────────────────────────────────────
// `publicMode` renders this same page at /club/charter. The only difference is
// where the charter is read from: /notices/charter is behind auth, so an
// anonymous visitor gets the public mirror instead.
export function CharterPage({ publicMode = false }) {
  const [charter, setCharter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfError, setPdfError] = useState(false);

  useEffect(() => {
    api.get(publicMode ? '/public/charter' : '/notices/charter')
      .then(res => {
        const notices = res.data.notices || [];
        const sorted = notices
          .filter(n => n.notice_type === 'charter')
          .sort((a, b) => {
            if (a.is_pinned && !b.is_pinned) return -1;
            if (!a.is_pinned && b.is_pinned) return 1;
            return new Date(b.created_at) - new Date(a.created_at);
          });
        setCharter(sorted[0] || null);
      })
      .catch(() => setCharter(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Header */}
      <div style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '14px 16px', flexShrink: 0 }}>
        <h2 style={{ color: '#F5C518', fontFamily: 'Rajdhani,sans-serif', marginBottom: 0, fontSize: '1.3rem' }}>
          📜 UCC Charter 2026
        </h2>
        {charter && (
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
            Updated {new Date(charter.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        )}
      </div>

      {charter ? (
        charter.file_url ? (
          /* ── PDF Viewer ── */
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Open in new tab button */}
            <div style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{charter.title}</span>
              <a
                href={charter.file_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: '#1E3A6E', color: 'white', borderRadius: 6, fontWeight: 700, textDecoration: 'none', fontSize: '0.78rem' }}
              >
                ↗ Open Full Screen
              </a>
            </div>

            {!pdfError ? (
              <iframe
                src={charter.file_url}
                title="UCC Charter 2026"
                style={{ flex: 1, width: '100%', border: 'none', minHeight: 0 }}
                onError={() => setPdfError(true)}
              />
            ) : (
              /* Fallback if iframe blocked */
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>📄</div>
                <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: '1.1rem', fontWeight: 700, color: '#1E3A6E', marginBottom: 8 }}>
                  {charter.title}
                </div>
                {charter.body && (
                  <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: 20, whiteSpace: 'pre-wrap', maxWidth: 400 }}>
                    {charter.body}
                  </p>
                )}
                <a
                  href={charter.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '12px 24px', background: '#1E3A6E', color: 'white', borderRadius: 8, fontWeight: 700, textDecoration: 'none', fontSize: '0.9rem' }}
                >
                  📄 Open Charter PDF
                </a>
              </div>
            )}
          </div>
        ) : (
          /* Charter posted but no PDF link — show text content */
          <div className="container" style={{ paddingTop: 'var(--space-lg)' }}>
            <div className="card">
              <div className="card__body" style={{ lineHeight: 1.8, color: 'var(--ucc-gray-700)', fontSize: '0.9rem' }}>
                <h3 style={{ fontFamily: 'Rajdhani,sans-serif', color: '#1E3A6E', marginBottom: 'var(--space-md)', fontSize: '1.15rem' }}>
                  {charter.title}
                </h3>
                <div style={{ whiteSpace: 'pre-wrap' }}>{charter.body}</div>
              </div>
            </div>
          </div>
        )
      ) : (
        /* No charter posted yet */
        <div className="container" style={{ paddingTop: 'var(--space-lg)' }}>
          <div className="card">
            <div className="card__body" style={{ textAlign: 'center', padding: '40px 24px' }}>
              <div style={{ fontSize: '3rem', marginBottom: 12 }}>📜</div>
              <h3 style={{ fontFamily: 'Rajdhani,sans-serif', color: '#1E3A6E', marginBottom: 12 }}>
                UCC Charter — Coming Soon
              </h3>
              <p style={{ color: '#6b7280', fontSize: '0.85rem', lineHeight: 1.6 }}>
                The Charter will appear here once posted by Admin via<br />
                <strong>Noticeboard → Post Notice → Type: 📜 Charter</strong>
              </p>
              <p style={{ marginTop: 16, color: '#9ca3af', fontSize: '0.8rem' }}>
                Covers: Member obligations · Fee structure · Fine categories ·<br />
                Attendance rules · Captaincy · Floater system · Disciplinary process
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PollsPage ────────────────────────────────────────────
export function PollsPage() {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState({});
  const [editing, setEditing] = useState({});   // pollId → true while changing a vote

  useEffect(() => {
    api.get('/polls').then(res => setPolls(res.data.polls || [])).finally(() => setLoading(false));
  }, []);

  async function submitPoll(pollId, response) {
    try {
      await api.post(`/polls/${pollId}/respond`, { response });
      toast.success(editing[pollId] ? 'Vote changed!' : 'Response recorded!');
      setPolls(p => p.map(poll => poll.id === pollId ? { ...poll, my_response: response } : poll));
      setEditing(e => ({ ...e, [pollId]: false }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit');
    }
  }

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ background:"white", borderBottom:"1px solid #e5e7eb", padding:"18px 16px 14px", position:"relative", overflow:"hidden" }}>
        <h2 style={{ color: '#F5C518', fontFamily: 'Rajdhani,sans-serif' }}>🗳️ Polls</h2>
      </div>
      <div className="container" style={{ paddingTop: 'var(--space-md)' }}>
        {polls.length === 0 ? (
          <div className="empty-state"><div className="empty-state__icon">🗳️</div><div className="empty-state__title">No active polls</div></div>
        ) : polls.map(poll => {
          const hasResponded = poll.my_response;
          const isPastDeadline = poll.deadline && new Date() > new Date(poll.deadline);
          const localResponse = responses[poll.id];

          return (
            <div key={poll.id} className="card mb-md">
              <div className="card__header">
                <span className="card__title" style={{ textTransform: 'none', fontSize: '1rem' }}>{poll.question}</span>
                {hasResponded && <span className="badge badge-success">VOTED</span>}
              </div>
              <div className="card__body">
                {poll.deadline && (
                  <div style={{ fontSize: '0.75rem', color: isPastDeadline ? 'var(--danger)' : 'var(--ucc-gray-500)', marginBottom: 'var(--space-sm)' }}>
                    {isPastDeadline ? '⛔ Closed' : `⏰ Closes ${new Date(poll.deadline).toLocaleString('en-IN', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}`}
                  </div>
                )}
                {(!hasResponded || editing[poll.id]) && !isPastDeadline && (
                  <>
                    {poll.response_type === 'yes_no' && (
                      <div className="toggle-group">
                        {['yes', 'no'].map(opt => (
                          <button key={opt} className={`toggle-btn ${localResponse === opt ? (opt === 'yes' ? 'selected-yes' : 'selected-no') : ''}`}
                            onClick={() => setResponses(r => ({ ...r, [poll.id]: opt }))}>
                            {opt === 'yes' ? '✅ YES' : '❌ NO'}
                          </button>
                        ))}
                      </div>
                    )}
                    {['multiple_choice', 'multi_select'].includes(poll.response_type) && (() => {
                      // multi_select: tick as many as you like; multiple_choice: pick one.
                      const multi = poll.response_type === 'multi_select';
                      const picked = multi ? (Array.isArray(localResponse) ? localResponse : []) : null;
                      const isOn = opt => multi ? picked.includes(opt) : localResponse === opt;
                      const pick = opt => setResponses(r => {
                        if (!multi) return { ...r, [poll.id]: opt };
                        const next = picked.includes(opt) ? picked.filter(x => x !== opt) : [...picked, opt];
                        return { ...r, [poll.id]: next.length ? next : undefined };
                      });
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {multi && <div style={{ fontSize: '0.75rem', color: 'var(--ucc-gray-500)' }}>Select all that apply</div>}
                          {(poll.options || []).map(opt => (
                            <button key={opt} onClick={() => pick(opt)}
                              style={{ padding: '10px 16px', borderRadius: 'var(--radius-md)', border: `2px solid ${isOn(opt) ? '#1E3A6E' : 'var(--ucc-gray-200)'}`,
                                background: isOn(opt) ? '#1E3A6E' : 'white', color: isOn(opt) ? 'white' : 'var(--ucc-gray-700)',
                                textAlign: 'left', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '0.9rem' }}>
                              {multi ? (isOn(opt) ? '☑ ' : '☐ ') : ''}{opt}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                    {poll.response_type === 'rating' && (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                        {[1,2,3,4,5].map(n => (
                          <button key={n} onClick={() => setResponses(r => ({ ...r, [poll.id]: n }))}
                            style={{ width: 48, height: 48, borderRadius: 'var(--radius-md)', border: `2px solid ${localResponse === n ? '#F5C518' : 'var(--ucc-gray-200)'}`,
                              background: localResponse === n ? '#F5C518' : 'white', fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.1rem', cursor: 'pointer' }}>
                            {n}
                          </button>
                        ))}
                      </div>
                    )}
                    {poll.response_type === 'short_answer' && (
                      <input className="form-control" placeholder="Your answer…" value={localResponse || ''}
                        onChange={e => setResponses(r => ({ ...r, [poll.id]: e.target.value }))} />
                    )}
                    {localResponse !== undefined && localResponse !== '' && (
                      <button className="btn btn-primary btn-block mt-md" onClick={() => submitPoll(poll.id, localResponse)}>
                        Submit Response
                      </button>
                    )}
                  </>
                )}
                {hasResponded && !editing[poll.id] && !isPastDeadline && (
                  <button className="btn btn-sm" style={{ float: 'right', fontSize: '0.75rem', padding: '4px 10px' }}
                    onClick={() => {
                      // Start from the current answer so a small change is one tap.
                      let cur = poll.my_response;
                      if (typeof cur === 'string') { try { cur = JSON.parse(cur); } catch (_) { /* plain text answer */ } }
                      setResponses(r => ({ ...r, [poll.id]: cur }));
                      setEditing(e => ({ ...e, [poll.id]: true }));
                    }}>
                    ✏️ Change vote
                  </button>
                )}
                {hasResponded && editing[poll.id] && (
                  <button className="btn btn-sm" style={{ fontSize: '0.75rem', padding: '4px 10px', marginTop: 8 }}
                    onClick={() => setEditing(e => ({ ...e, [poll.id]: false }))}>
                    Cancel
                  </button>
                )}
                {hasResponded && !editing[poll.id] && (
                  <div style={{ color: 'var(--success)', fontSize: '0.85rem' }}>
                    ✅ Your response: <strong>{Array.isArray(poll.my_response) ? poll.my_response.join(', ') : typeof poll.my_response === 'string' ? poll.my_response.replace(/"/g, '') : String(poll.my_response)}</strong>
                  </div>
                )}
                <div style={{ fontSize: '0.75rem', color: 'var(--ucc-gray-400)', marginTop: 8 }}>
                  {poll.response_count || 0} response(s) • {poll.created_by_name}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── ReimbursementsPage ───────────────────────────────────
export function ReimbursementsPage() {
  const [reimbursements, setReimbursements] = useState([]);
  const [showing, setShowing] = useState(false);
  const [form, setForm] = useState({ amount: '', description: '', category: '' });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/reimbursements/my').then(res => setReimbursements(res.data.reimbursements || [])).finally(() => setLoading(false));
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (!form.amount) return toast.error('Amount required');
    setSubmitting(true);
    try {
      const res = await api.post('/reimbursements', form);
      toast.success('Reimbursement submitted!');
      setReimbursements(r => [res.data.reimbursement, ...r]);
      setShowing(false);
      setForm({ amount: '', description: '', category: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  }

  const statusColors = { pending: 'var(--warning)', approved: 'var(--info)', rejected: 'var(--danger)', settled: 'var(--success)' };

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ background:"white", borderBottom:"1px solid #e5e7eb", padding:"18px 16px 14px", position:"relative", overflow:"hidden" }}>
        <h2 style={{ color: '#F5C518', fontFamily: 'Rajdhani,sans-serif' }}>🧾 Reimbursements</h2>
      </div>
      <div className="container" style={{ paddingTop: 'var(--space-md)' }}>
        <button className="btn btn-primary btn-block mb-md" onClick={() => setShowing(!showing)}>
          {showing ? 'Cancel' : '+ Submit New Claim'}
        </button>
        {showing && (
          <form onSubmit={submit} className="card mb-md">
            <div className="card__body">
              <div className="form-group">
                <label className="form-label">Amount (₹) *</label>
                <input type="number" className="form-control" placeholder="Amount" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} required inputMode="numeric" />
                {parseFloat(form.amount) > 5000 && <div style={{ fontSize: '0.75rem', color: 'var(--warning)', marginTop: 4 }}>⚠️ Over ₹5,000 requires 2 Management Board approvals</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Category</label>
                <input type="text" className="form-control" placeholder="e.g. Ground equipment, First aid..." value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" placeholder="Describe what you spent on..." value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} />
              </div>
              <button type="submit" className="btn btn-gold btn-block" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Claim'}
              </button>
            </div>
          </form>
        )}
        {reimbursements.length === 0 ? (
          <div className="empty-state"><div className="empty-state__icon">🧾</div><div className="empty-state__title">No claims yet</div></div>
        ) : (
          <div className="card">
            {reimbursements.map(r => (
              <div key={r.id} className="list-item">
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{r.category || 'Reimbursement'}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--ucc-gray-500)' }}>{r.description}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--ucc-gray-400)', marginTop: 2 }}>{new Date(r.created_at).toLocaleDateString('en-IN')}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800 }}>₹{parseFloat(r.amount).toLocaleString('en-IN')}</div>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: statusColors[r.status] || 'var(--ucc-gray-500)', textTransform: 'uppercase' }}>{r.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default NoticePage;
